import React from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ShellPanel } from "./ShellContext";
import type { PanelSection } from "./nav";

/** A link in a views panel: white card with a hairline when it is the current page */
export function PanelNavLink({ to, children, count, className }: { to: string; children: React.ReactNode; count?: number | string; className?: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          "flex h-8 items-center justify-between gap-2 rounded-md px-2.5 text-[13px] transition-colors",
          isActive ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          className,
        )
      }
    >
      <span className="truncate">{children}</span>
      {count !== undefined && <span className="font-mono text-[11.5px] text-muted-foreground">{count}</span>}
    </NavLink>
  );
}

export function PanelHeading({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("px-2.5 pb-1 pt-4 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground", className)}>{children}</div>;
}

/**
 * Sub-navigation for Settings and Users & roles, shown in the views panel. Compact rows, and each group is its own block:
 * a hairline above its heading (the heading is bolder than the links), so a long list reads as a few clear sections.
 */
export function SectionPanel({ title, sections }: { title: string; sections: PanelSection[] }) {
  return (
    <ShellPanel title={title}>
      {sections.map((section, i) => (
        <div key={section.title || i} className={cn("flex flex-col gap-px", i > 0 && "mt-2.5 border-t border-border pt-0.5")}>
          {section.title && <PanelHeading className="pb-1 pt-2.5 text-[10px] tracking-[0.1em] text-foreground/70">{section.title}</PanelHeading>}
          {section.links.map((link) => (
            <PanelNavLink key={link.to} to={link.to} className="h-7 text-[12.5px]">
              {link.label}
            </PanelNavLink>
          ))}
        </div>
      ))}
    </ShellPanel>
  );
}
